import { SmsRcsMessagingTermsContent } from "./SmsRcsMessagingTerms";
import type { DocMeta } from "./types";

const SLUG = "messaging-terms";
const id = (s: string) => `${SLUG}-${s}`;

export const MESSAGING_TERMS_META: DocMeta = {
  slug: SLUG,
  title: "Messaging Terms",
  subtitle:
    "How Logical Health's SMS/RCS and other messaging programs work, including opt-in, HELP/STOP, and consent",
  description:
    "How Logical Health's SMS/RCS and other messaging programs work, including opt-in, HELP/STOP, and consent.",
  lastUpdated: "July 3, 2026",
  sections: [
    { id: id("introduction"), title: "Introduction" },
    { id: id("sms-rcs"), title: "1. SMS/RCS Messaging Terms" },
    { id: id("other-channels"), title: "2. Other Messaging Channels" },
    { id: id("eligibility"), title: "3. Eligibility" },
    {
      id: id("customer-deployments-and-baas"),
      title: "4. Customer Deployments and BAAs",
    },
    { id: id("changes-to-these-terms"), title: "5. Changes to These Terms" },
    { id: id("contact"), title: "6. Contact" },
  ],
};

export function MessagingTerms() {
  return (
    <>
      <section id={id("introduction")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction</h2>
        <p>
          These Messaging Terms describe how Logical Health, Inc.
          (&ldquo;Logical Health&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
          and &ldquo;our&rdquo;) operates SMS/RCS and other two-way messaging
          programs. They sit alongside our{" "}
          <a
            href="/legal#privacy-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Privacy Policy
          </a>
          ,{" "}
          <a
            href="/legal#terms-of-service"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Terms of Service
          </a>
          , and{" "}
          <a
            href="/cookie-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Cookie Policy
          </a>
          . When messaging programs are operated for customer deployments
          involving PHI/ePHI, the program is also governed by the applicable
          Business Associate Agreement, customer instructions, HIPAA/HITECH,
          and law as described in our{" "}
          <a
            href="/hipaa-compliance"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            HIPAA &amp; Security Statement
          </a>
          .
        </p>
      </section>

      <section id={id("sms-rcs")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          1. SMS/RCS Messaging Terms
        </h2>
        <SmsRcsMessagingTermsContent
          makeId={(suffix) => id(`sms-rcs-${suffix}`)}
        />
      </section>

      <section id={id("other-channels")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          2. Other Messaging Channels
        </h2>
        <p>
          Logical Health may also offer WhatsApp or similar messaging
          channels for eligible users and customer deployments.
          Channel-specific terms, disclosures, and consent flows are
          presented when you enroll in those programs, and opt-out and help
          controls equivalent to STOP and HELP are honored on each channel.
          Message and data rates may apply based on your carrier and plan.
        </p>
      </section>

      <section id={id("eligibility")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          3. Eligibility
        </h2>
        <p>
          Logical Health messaging programs are intended for users who are at
          least 18 years old and located in jurisdictions where the relevant
          program is offered. Carrier participation, regional support, and
          feature availability may vary.
        </p>
      </section>

      <section id={id("customer-deployments-and-baas")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          4. Customer Deployments and BAAs
        </h2>
        <p>
          When Logical Health operates a messaging program as part of a
          customer deployment involving PHI/ePHI, the program is governed by
          the applicable Business Associate Agreement, customer instructions,
          HIPAA/HITECH, and law in addition to these Messaging Terms.
          Customer-controlled message content, consent records, and PHI/ePHI
          handling are governed by the applicable customer agreement.
        </p>
      </section>

      <section id={id("changes-to-these-terms")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          5. Changes to These Terms
        </h2>
        <p>
          We may update these Messaging Terms from time to time to reflect
          changes in our messaging programs, carriers, vendors, legal
          requirements, or operational practices. When we make changes, we
          will post the updated Messaging Terms on this page and update the
          &ldquo;Last Updated&rdquo; date.
        </p>
      </section>

      <section id={id("contact")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Contact</h2>
        <p>
          For privacy, consent, opt-in, or opt-out inquiries about Logical
          Health messaging programs, contact{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>
          . For general questions, contact{" "}
          <a
            href="mailto:hello@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            hello@logical.health
          </a>
          .
        </p>
        <p className="mt-4">
          For help, reply HELP or contact{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>
          . Message frequency varies. Msg &amp; data rates may apply. Reply
          STOP to opt out.
        </p>
      </section>
    </>
  );
}
